import { Flatfile } from "@flatfile/api";
import { BATCH_LABELS_WS_KEYS } from "@wingspanhq/payments/dist/lib/constants";
import { Batch } from "@wingspanhq/wingspan-api-sdk";
import { WSQueryConfig } from "@ws-react-query";

import { useWSQuery } from "../../../../query/helpers";
import { FLATFILE_UPLOAD_JOB_REFETCH_INTERVAL } from "../../constants";
import { useFlatfileApiClient } from "../../hooks/useFlatfileApiClient";
import { selectorIsImportBatchUploadInProgress } from "../../selectors/selectorIsImportBatchUploadInProgress";
import { getFlatfileMainSheetId } from "../../utils/getFlatfileMainSheetId";
import {
  QUERY_FLATFILE_SKIPPED_ROWS_COUNT,
  QUERY_FLATFILE_UPLOAD_JOB
} from "./keys";

const FINISHED_UPLOAD_JOB_STATUSES: Flatfile.JobStatus[] = [
  Flatfile.JobStatus.Complete,
  Flatfile.JobStatus.Failed,
  Flatfile.JobStatus.Canceled
];

const isUploadJobFinished = (uploadJob?: Flatfile.Job): boolean =>
  !!uploadJob?.status &&
  FINISHED_UPLOAD_JOB_STATUSES.includes(uploadJob.status);

/**
 * Flatfile job that uploads the spreadsheet rows into the batch (the parent
 * "main action" job). It is the only source of upload progress and of upload
 * failures: when the job dies, the batch keeps its `flatfileJobId` metadata and
 * never receives `uploadFinishedAt`.
 */
export const useQueryFlatfileUploadJob = (
  batch?: Batch,
  config?: WSQueryConfig<Flatfile.Job, Error>
) => {
  const flatfileApiClient = useFlatfileApiClient(batch);
  const jobId = batch?.metadata?.[BATCH_LABELS_WS_KEYS.flatfileJobId];

  return useWSQuery<Flatfile.Job, Error>(
    [QUERY_FLATFILE_UPLOAD_JOB, jobId],
    async () => {
      const { data: job } = await flatfileApiClient!.jobs.get(jobId!);
      return job;
    },
    {
      // The job is only interesting while the upload runs; afterwards the batch
      // metadata tells the whole story, so finished batches must not hit
      // Flatfile on every detail page view.
      enabled:
        !!flatfileApiClient &&
        !!jobId &&
        selectorIsImportBatchUploadInProgress(batch),
      refetchInterval: uploadJob =>
        selectorIsImportBatchUploadInProgress(batch) &&
        !isUploadJobFinished(uploadJob)
          ? FLATFILE_UPLOAD_JOB_REFETCH_INTERVAL
          : false,
      retry: false,
      ...config
    }
  );
};

/**
 * Number of uploaded spreadsheet rows that did not make it into the batch.
 *
 * Counted in Flatfile rather than read off the `flatfileRejectedRowsCount`
 * batch metadata, because that number only counts rows the Wingspan API
 * refused. Rows that failed validation are never submitted at all, so they are
 * missing from it even though they are exactly the rows the customer sees as
 * skipped. Error state covers both, and is the same state the skipped-rows CSV
 * downloads, so the number and the file can never disagree.
 *
 * Callers should still fall back to the metadata when this returns undefined:
 * the count needs a live Flatfile space, and the download is equally
 * unavailable once that space is gone.
 */
export const useQueryFlatfileSkippedRowsCount = (
  batch?: Batch,
  sheetSlug?: string,
  config?: WSQueryConfig<number, Error>
) => {
  const flatfileApiClient = useFlatfileApiClient(batch);
  const spaceId = batch?.metadata?.[BATCH_LABELS_WS_KEYS.flatfileBatchId];

  return useWSQuery<number, Error>(
    [QUERY_FLATFILE_SKIPPED_ROWS_COUNT, spaceId],
    async () => {
      const sheetId = await getFlatfileMainSheetId(
        flatfileApiClient!,
        batch!,
        sheetSlug!
      );
      const { data } = await flatfileApiClient!.sheets.getRecordCounts(sheetId);
      return data.counts.error;
    },
    {
      // While the upload is still running the number is meaningless — parts are
      // still submitting rows and enriching rejections — so this only runs once
      // the upload has come to rest.
      enabled:
        !!flatfileApiClient &&
        !!spaceId &&
        !!sheetSlug &&
        !selectorIsImportBatchUploadInProgress(batch),
      retry: false,
      ...config
    }
  );
};
